import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { ReplaySubject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { MenuColorScheme } from '../interfaces/menu-color-scheme.interface';
import { ApiService } from './api.service';
import { LoadingSpinnerService } from './loading-spinner.service';
import { StorageService } from './storage.service';
import { UtilityService } from './utility.service';

@Injectable({
  providedIn: 'root',
})
export class AppService {
  public menuColorSchemes = MenuColorScheme;

  public static readonly APP_VERSION: string = 'V.1.3.5';
  public areCoreFeaturesLoaded$: ReplaySubject<any> = new ReplaySubject();
  public areCoreFeaturesLoaded: boolean = false;
  public isMobileToolbarShown: boolean = true;
  public menuColorScheme: MenuColorScheme = MenuColorScheme.RED;

  get appVersion() {
    return AppService.APP_VERSION;
  }

  get isNativePlatform() {
    return Capacitor.isNativePlatform();
  }

  get showMobileLayout() {
    return Capacitor.isNativePlatform() || document.body.clientWidth < 900;
  }

  get isPartnerVersion() {
    return environment.isPartnerVersion;
  }

  get isEmkaOilVersion() {
    return environment.isEmkaOilVersion;
  }

  constructor(
    private loadingSpinnerService: LoadingSpinnerService,
    private apiService: ApiService,
    private storageService: StorageService,
    private http: HttpClient,
    private utilityService: UtilityService,
  ) {}

  public async resetLogout() {
    if (!(await this.loadingSpinnerService.lockAnimateQuery())) {
      return;
    }

    try {
      if (!this.isNativePlatform) {
        this.storageService.clear();
        window.location.href =
          window.location.protocol +
          '//' +
          window.location.host +
          '/authorization';
      } else {
        await this.deactivateNativeApp();
        this.storageService.clear();
        window.location.href =
          window.location.protocol +
          '//' +
          window.location.host +
          '/activation';
      }
    } catch (error) {
      await this.loadingSpinnerService.unlockStopAnimateQuery();
      this.utilityService.showErrorToast(error);
    }
  }

  private async deactivateNativeApp(): Promise<void> {
    const token = this.storageService.get('token');
    const lang = this.storageService.getPersistent('lang');
    const api = this.apiService.api;

    let url = api + '/api/Aktywacja/Dezaktywuj?';
    let urlParams = new HttpParams();
    urlParams = urlParams.set('Jezyk', lang);
    url = url + urlParams.toString();

    const requestBody = {
      numerTelefonu: this.storageService.get('phoneNumber'),
      kodAOK: this.storageService.get('activationData').kodAOK,
      uuid: token,
    };

    return this.http
      .request<any>('post', url, {
        body: requestBody,
        headers: {
          Token: token,
        },
      })
      .toPromise();
  }

  public async deleteAccount(): Promise<void> {
    if (!(await this.loadingSpinnerService.lockAnimateQuery())) {
      return;
    }

    try {
      await this.deleteAccountOnServer();

      this.storageService.clear();
      window.location.href =
        window.location.protocol +
        '//' +
        window.location.host +
        '/authorization';
    } catch (error) {
      await this.loadingSpinnerService.unlockStopAnimateQuery();
      this.utilityService.showErrorToast(error);
    }
  }

  private deleteAccountOnServer(): Promise<void> {
    const token = this.storageService.get('token');
    const lang = this.storageService.getPersistent('lang');
    const api = this.apiService.api;

    let url = api + '/api/Uzytkownik/UsunKonto?';
    let urlParams = new HttpParams();
    urlParams = urlParams.set('Jezyk', lang);
    url = url + urlParams.toString();

    return this.http
      .request<any>('post', url, {
        headers: {
          Token: token,
        },
      })
      .toPromise();
  }
}
